import { useEffect, useState } from 'react';
import { ROOM_ID_RE, type ParticipantView } from '@voxa/shared';
import { loadCapabilities, reloadCapabilities, type Capabilities } from './lib/capabilities.ts';
import { api } from './lib/api.ts';
import { Home } from './screens/Home.tsx';
import { Login } from './screens/Login.tsx';
import { Join } from './screens/Join.tsx';
import { Call } from './screens/Call.tsx';
import { MicCheck } from './components/MicCheck.tsx';
import { useT } from './i18n/index.tsx';
import { LanguagePicker } from './i18n/LanguagePicker.tsx';

/**
 * Two routes and three steps, so there is no router here: a path match and a
 * state machine are smaller than a dependency, and the whole app is one flow.
 *
 *   /             -> create a room  -> check audio -> call
 *   /join/:roomId -> accept invite  -> check audio -> call
 */
type Stage =
  | { name: 'home' }
  | { name: 'join'; roomId: string }
  | { name: 'check'; participant: ParticipantView }
  | { name: 'call'; participant: ParticipantView; micStream: MediaStream };

export function App() {
  const t = useT();
  const [stage, setStage] = useState<Stage>(() => {
    const match = /^\/join\/([0-9a-f]{12})\/?$/.exec(location.pathname);
    const roomId = match?.[1];
    return roomId && ROOM_ID_RE.test(roomId) ? { name: 'join', roomId } : { name: 'home' };
  });
  const [caps, setCaps] = useState<Capabilities | null>(null);
  // Whether the answer has arrived at all, as distinct from arriving empty: a
  // server that will not say is not a reason to keep anyone out.
  const [capsKnown, setCapsKnown] = useState(false);

  // Asked for here, at the top of the app, because this is the earliest point
  // at which anything needs it — and because the answer is cached, which is
  // what lets the call read it later without a request of its own.
  useEffect(() => {
    void loadCapabilities().then((c) => {
      setCaps(c);
      setCapsKnown(true);
    });
  }, []);

  return (
    <div className="app">
      <header className="brand">
        <div>
          Voxa <span>{t.common.tagline}</span>
        </div>
        <LanguagePicker />
      </header>

      {caps?.provider.configured === false && (
        <div className="card">
          <div className="notice bad">{t.banner.providerUnconfigured}</div>
        </div>
      )}

      {/* Task 18: the creation screen is behind the demo code. The join route
          is not — an invitation is the guest's way in. */}
      {stage.name === 'home' && capsKnown && caps?.accessCodeRequired && caps.host === false && (
        <Login onDone={() => void reloadCapabilities().then(setCaps)} />
      )}

      {stage.name === 'home' && capsKnown && !(caps?.accessCodeRequired && caps.host === false) && (
        <Home
          onSignOut={
            caps?.accessCodeRequired
              ? () =>
                  void api
                    .logout()
                    .catch(() => undefined)
                    .then(() => reloadCapabilities())
                    .then(setCaps)
              : undefined
          }
          sourceSubtitlesAvailable={caps?.provider.sourceTranscription === true}
          voicePickerAvailable={caps?.voice?.providers.includes('elevenlabs') === true}
          onCreated={(room) => setStage({ name: 'check', participant: room })}
        />
      )}

      {stage.name === 'join' && (
        <Join
          roomId={stage.roomId}
          onJoined={(participant) => setStage({ name: 'check', participant })}
        />
      )}

      {stage.name === 'check' && (
        <div className="card">
          <h1>{t.check.title}</h1>
          <p className="lede">{t.check.lede}</p>
          <MicCheck
            busy={false}
            onReady={(micStream) =>
              setStage({ name: 'call', participant: stage.participant, micStream })
            }
          />
        </div>
      )}

      {stage.name === 'call' && (
        <Call
          participant={stage.participant}
          micStream={stage.micStream}
          onFinished={() => {
            // A new conversation is a new room, so go back to the start rather
            // than trying to revive a closed one.
            location.href = '/';
          }}
        />
      )}
    </div>
  );
}
